import React from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Skeleton
} from '@mui/material';
import PlacesAutocomplete from 'react-places-autocomplete';

const LocationHoursForm = ({ formData, onChange, isLoading, errors = {} }) => {
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={120} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  const handleAddressChange = (address) => {
    onChange({
      target: {
        name: 'address',
        value: address
      }
    });
  };

  const handleAddressSelect = async (address) => {
    try {
      handleAddressChange(address);
    } catch (error) {
      console.error('Error selecting address:', error);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Location & Hours
      </Typography>

      <Grid container spacing={3}>

        {/* Business Address */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Business Address
          </Typography>
          <PlacesAutocomplete
            value={formData.address || ''}
            onChange={handleAddressChange}
            onSelect={handleAddressSelect}
          >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
              <div className="dropdown-container">
                <TextField
                  {...getInputProps({
                    placeholder: 'Enter business address',
                    variant:     'outlined',
                    fullWidth:   true,
                    required:    true,
                    error:       !!errors.address,
                    helperText:  errors.address
                  })}
                />
                {suggestions.length > 0 && (
                  <Paper className="dropdown-suggestions">
                    {loading && <div>Loading...</div>}
                    {suggestions.map((suggestion) => {
                      const { key, ...suggestionProps } = getSuggestionItemProps(suggestion, {
                        className: 'suggestion-item',
                      });
                      return (
                        <div key={suggestion.placeId} {...suggestionProps}>
                          {suggestion.description}
                        </div>
                      );
                    })}
                  </Paper>
                )}
              </div>
            )}
          </PlacesAutocomplete>
        </Grid>

        {/* Google Place ID */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="googlePlaceId"
            label="Google Place ID (Optional)"
            value={formData.googlePlaceId || ''}
            onChange={onChange}
            variant="outlined"
            placeholder="e.g. ChIJN1t_tDeuEmsRUsoyG83frY4"
            helperText="Used to pull your Google reviews automatically. Find yours at: maps.google.com → search your business → Share → Copy link"
          />
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="caption"
              component="a"
              href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'none' }}
            >
              How to find your Place ID ↗
            </Typography>
          </Box>
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Additional Information (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            name="additionalInfo"
            value={formData.additionalInfo || ''}
            onChange={onChange}
            variant="outlined"
            placeholder="Add any additional information about your business location, hours of operation, special occasions, or access instructions (e.g., 'Open Mon-Fri 9AM-5PM', 'Closed on holidays', 'Second floor, Suite 201')"
            helperText="Include business hours, special schedules, location details, or any other relevant information"
          />
        </Grid>

      </Grid>
    </Box>
  );
};

export default LocationHoursForm;